// src/components/Dashboard.jsx
import React, { useEffect, useState, useMemo, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import CalendarWidget from "../../CalendarWidget";
import dayjs from "dayjs";
import LoadingErrorComponent from "../../LoadingError/LoadingError";
import { Doughnut } from "react-chartjs-2";
import user from "../../../assets/image/user.jpg";
import logo from "../../../assets/image/kkgt-header-image.jpg";

import {
  FaCalendarCheck,
  FaClock,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaChartPie,
  FaChartBar,
  FaCalendarAlt,
} from "react-icons/fa";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import AppointmentsDashboard from "./AppointmentsDashboard";
import SuperPatientProfile from "./SuperPatientProfile";

ChartJS.register(ArcElement, Tooltip, Legend);

const Dashboard = () => {
  const {
    appointments,
    appointmentsLoading,
    appointmentsError,
    getUpcomingAppointments,
    getAllPateintData,
    allPatients,
    getOPDData,
    getDoctorDetails,
    doctorDetails,
    loginUser,
  } = useContext(AuthContext);


  const [isSuperPatientProfileOpen, setIsSuperPatientProfileOpen] = useState(false);
  const [APIcallLoading, setAPIcallLoading] = useState(false);
  const [profileId, setProfileId] = useState(null);

  const today = new Date();
  const [childDate, setChildDate] = useState(today);
  const [dataRefreshTime, setDataRefreshTime] = useState(new Date());

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!appointments || appointments.length === 0) {
          await getUpcomingAppointments();
        }
        if (!allPatients || allPatients.length === 0) {
          await getAllPateintData();
        }
        await getOPDData();
        if (loginUser?.id && !doctorDetails) {
          await getDoctorDetails(loginUser.id);
        }
        setDataRefreshTime(new Date());
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
    };

    loadInitialData();
  }, []);

  const handleDataFromChild = (data) => {
    setChildDate(data);
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A";
    return dayjs(isoDate).format("MMMM D, YYYY");
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) {
      hours += 12;
    }
    if (modifier === "AM" && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  };

  // Memoized appointment calculations
  const todayAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = new Date(appointment.date);
        appointmentDate.setHours(0, 0, 0, 0);
        return appointmentDate.getTime() === today.getTime();
      })
      .sort((a, b) => {
        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments]);

  const selectedDateAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = new Date(appointment.date);
        const selectedDate = new Date(childDate);

        return (
          appointmentDate.getFullYear() === selectedDate.getFullYear() &&
          appointmentDate.getMonth() === selectedDate.getMonth() &&
          appointmentDate.getDate() === selectedDate.getDate()
        );
      })
      .sort((a, b) => {
        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments, childDate]);

  const parseDate = (isoDate) => {
    if (!isoDate) return new Date();
    const date = new Date(isoDate);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const upcomingAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];

    const today = parseDate(new Date());

    return appointments
      .filter((appointment) => {
        if (!appointment.date) return false;
        const appointmentDate = parseDate(appointment.date);
        return appointmentDate > today;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);

        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }

        const timeA = parseTime(a.timeBlock?.split(" - ")[0] || "");
        const timeB = parseTime(b.timeBlock?.split(" - ")[0] || "");
        return timeA - timeB;
      });
  }, [appointments]);

  // Appointment status calculations
  const pendingAppointmentsCount = useMemo(() => {
    if (!appointments || appointments.length === 0) return 0;
    return appointments.filter(
      (appointment) => appointment.status?.toLowerCase() === "pending"
    ).length;
  }, [appointments]);

  const completedAppointmentsCount = useMemo(() => {
    if (!appointments || appointments.length === 0) return 0;
    return appointments.filter(
      (appointment) => appointment.status?.toLowerCase().trim() === "completed"
    ).length;
  }, [appointments]);

  // Statistics calculations
  const totalAppointments = appointments?.length || 0;
  const totalCompletedAppointments = completedAppointmentsCount;
  const todaysTotalAppointments = todayAppointments.length;
  const upcomingTotalAppointments = upcomingAppointments.length;
  const totalPendingAppointments = pendingAppointmentsCount;

  // Chart data
  const pieData = {
    labels: [
      // "Total Appointments",
      "Completed",
      "Today's",
      "Upcoming",
      "Pending",
    ],
    datasets: [
      {
        data: [
          // totalAppointments,
          totalCompletedAppointments,
          todaysTotalAppointments,
          upcomingTotalAppointments,
          totalPendingAppointments,
        ],
        backgroundColor: [
          // "#10B981",
          "#3B82F6",
          "#8B5CF6",
          "#F59E0B",
          "#EF4444",
        ],
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppointmentsDashboard />


      {/* Main Content */}
      <div className="px-6 py-6 space-y-6">
        {/* Statistics */}


        {/* Calendar Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar Widget */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
              <h2 className="text-xl font-bold text-white flex items-center">
                <FaCalendarAlt className="mr-3" />
                Calendar
              </h2>
            </div>
            <div className="p-4">
              <div style={{ height: "400px", width: "100%" }}>
                <CalendarWidget sendDataToParent={handleDataFromChild} />
              </div>
            </div>
          </div>

          {/* Selected Date Appointments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                {childDate ? formatDate(childDate) : "Selected Date"}
              </h2>
              <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                {selectedDateAppointments.length}
              </span>
            </div>

            <div className="max-h-80 overflow-y-auto">
              <LoadingErrorComponent
                loading={appointmentsLoading}
                error={appointmentsError}
              >
                {selectedDateAppointments.length === 0 ? (
                  <div className="text-center py-12">
                    <FaCalendarAlt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      No appointments for this date
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedDateAppointments.map((appointment) => (
                      <div
                        key={appointment._id}
                        className="p-4 border border-gray-100 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-900"
                              onClick={() => {
                                setProfileId(appointment.userId)
                                setIsSuperPatientProfileOpen(true)
                                console.log("user id", appointment.userId)
                              }}>
                              {appointment.name}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {appointment.reason}
                            </p>
                            <div className="flex items-center mt-2">
                              <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded text-xs">
                                {appointment.timeBlock}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </LoadingErrorComponent>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <SuperPatientProfile
        open={isSuperPatientProfileOpen}
        onClose={() => setIsSuperPatientProfileOpen(false)}
        patientId={profileId}
      />


    </div>
  );
};

export default Dashboard;
